import { FC, useContext } from 'react'
import { LLM, OpenRouterLLM } from '@/types'
import {
  IconBrain,
  IconCheck,
  IconCurrencyDollar,
  IconEye,
  IconHistory,
  IconPuzzle,
  IconTools
} from '@tabler/icons-react'
import { ImageIcon, WrenchIcon } from 'lucide-react'
import { useTheme } from 'next-themes'

import { CHAT_SETTING_LIMITS } from '@/lib/chat-setting-limits'
import { PLAN_FREE } from '@/lib/stripe/config'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { ChatbotUIContext } from '@/context/context'
import { validatePlanForModel } from '@/lib/subscription'
import { WithTooltip } from '@/components/ui/with-tooltip'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

import { ModelIcon } from './model-icon'

interface ModelOptionProps {
  model: LLM | OpenRouterLLM
  onSelect: () => void
  selected: boolean
  recent?: boolean
}

export const ModelOption: FC<ModelOptionProps> = ({ model, selected, onSelect, recent }) => {
  const { profile, setIsPaywallOpen } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )
  const { theme } = useTheme()

  let contextLength = CHAT_SETTING_LIMITS[model.modelId]?.MAX_CONTEXT_LENGTH || 0

  if ('maxContext' in model) {
    contextLength = model.maxContext
  }
  const isLocked =
    model.tier && model.tier !== PLAN_FREE && !validatePlanForModel(profile as any, model.modelId)

  const handleClick = () => {
    if (isLocked) {
      scheduleUpgradePaywall()
      return
    }
    onSelect()
  }

  return (
    <div
      className="hover:bg-accent flex w-full cursor-pointer justify-start space-x-3 truncate rounded p-2"
      onClick={handleClick}
    >
      <div className="flex w-full items-center justify-between space-x-2">
        <div className={'relative flex items-center space-x-2'}>
          {recent ? (
            <IconHistory
              className={cn(
                'rounded-sm bg-white p-1 text-black',
                theme === 'dark' ? 'bg-white' : 'border-foreground/10 border'
              )}
              stroke={1.5}
              size={24}
            />
          ) : (
            <ModelIcon provider={model.provider} modelId={model?.modelId} width={24} height={24} />
          )}
          <div className={'text-sm ' + (selected ? 'font-semibold' : 'font-normal')}>
            {model.modelName}
          </div>
          {model.tier && model.tier !== PLAN_FREE && (
            <Badge variant={'outline'} className={'h-5 text-xs capitalize'}>
              {model.tier}
            </Badge>
          )}
          {/* No extra Upgrade badge; tier already communicates access level */}
          {/* {model.modelId === 'losi-omega' && (
            <Badge variant={"outline"} className="h-5 text-xs">Pro</Badge>
          )} */}
          {/* Remove Mini badge */}
          {/* {model.modelId === 'losi-mini' && (
            <Badge className="h-5 bg-emerald-600 text-xs">Try</Badge>
          )} */}
          {/* Hide New badge for legacy models */}
          {model.new &&
            !['gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano'].includes(model.modelId as string) && (
              <Badge className="h-5 bg-violet-700 text-xs">New</Badge>
            )}
          {model.imageInput && (
            <Badge
              variant="outline"
              className="flex size-5 items-center justify-center rounded-full p-0"
            >
              <ImageIcon className="size-3" />
            </Badge>
          )}
          {model.tools && (
            <Badge
              variant="outline"
              className="flex size-5 items-center justify-center rounded-full p-0"
            >
              <WrenchIcon className="size-3" />
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {selected && <IconCheck size={18} stroke={1.5} />}
        </div>
        {/*<div className={"flex items-center space-x-1"}>*/}
        {/*  {model.imageInput && (*/}
        {/*    <WithTooltip*/}
        {/*      side="top"*/}
        {/*      display={"Vision supported"}*/}
        {/*      trigger={*/}
        {/*        <div className="w-4 text-xs font-normal opacity-75">*/}
        {/*          <IconEye stroke={2} size={16} />*/}
        {/*        </div>*/}
        {/*      }*/}
        {/*    />*/}
        {/*  )}*/}
        {/*  {model.tools && (*/}
        {/*    <WithTooltip*/}
        {/*      side="top"*/}
        {/*      display={"Plugins supported"}*/}
        {/*      trigger={*/}
        {/*        <div className="w-4 text-xs font-normal opacity-75">*/}
        {/*          <IconPuzzle stroke={2} size={16} />*/}
        {/*        </div>*/}
        {/*      }*/}
        {/*    />*/}
        {/*  )}*/}
        {/*  {contextLength && (*/}
        {/*    <WithTooltip*/}
        {/*      side="top"*/}
        {/*      display={*/}
        {/*        "Context length: " + (contextLength / 1000).toFixed(0) + "k"*/}
        {/*      }*/}
        {/*      trigger={*/}
        {/*        <div className="flex w-12 text-xs font-normal opacity-75">*/}
        {/*          <IconBrain stroke={2} size={16} />*/}
        {/*          {(contextLength / 1000).toFixed(0)}k*/}
        {/*        </div>*/}
        {/*      }*/}
        {/*    />*/}
        {/*  )}*/}
        {/*</div>*/}
      </div>
    </div>
  )
}
